/**
 * Step-specific form data processors
 */

import { appendObjectToFormData, appendAddressToFormData, appendFileOrUrl } from './formDataSanitizer';
import { validateFileType } from '../validations/fileValidation';
import { FILE_TYPES } from '../constants/fileTypes';
import { toast } from 'react-toastify';

/**
 * Process personal information step data
 */
export const processPersonalInfoStep = (formData, stepData) => {
	if (!stepData) return;
	
	// Process personal info object
	if (stepData.personalInfo) {
		Object.entries(stepData.personalInfo).forEach(([key, value]) => {
			if (key === 'applicationId') return;
			
			// Handle nested address objects
			if (key === 'mailingAddress' && typeof value === 'object' && value !== null) {
				appendAddressToFormData(formData, value, 'mailing');
				return;
			}
			
		if (key === 'permanentAddress' && typeof value === 'object' && value !== null) {
			appendAddressToFormData(formData, value, 'permanent');
			return;
		}
		
		// Helper function to format date as YYYY-MM-DD without timezone conversion
		const formatDateForServer = (date) => {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		};
		
		// Handle regular fields
		const sanitizedValue = value === null ? '' : 
			value instanceof Date ? formatDateForServer(value) :
			typeof value === 'object' && !(value instanceof File) ? JSON.stringify(value) :
			value;
		
		formData.append(`personalInfo_${key}`, sanitizedValue);
	});
}	// Handle files
	appendFileOrUrl(formData, stepData.image, 'personalInfo_photo', null);
	appendFileOrUrl(formData, stepData.document, 'personalInfo_document', null);
};

/**
 * Process educational background step data
 */
export const processEducationStep = (formData, stepData, originalData = null) => {
	if (!stepData || Object.keys(stepData).length === 0) return;
	
	// Process education fields
	appendObjectToFormData(formData, stepData, 'education', ['file']);
	
	// Handle education file
	let fileToUse = stepData.file;
	if (!fileToUse && originalData?.file) {
		fileToUse = originalData.file;
	}
	
	if (fileToUse) {
		if (fileToUse instanceof File) {
			const validation = validateFileType(fileToUse, FILE_TYPES.CERTIFICATE, 5);
			if (!validation.isValid) {
				toast.error(`Education Certificate Error: ${validation.error}`);
				return false;
			}
			formData.append('education_file', fileToUse);
		} else if (typeof fileToUse === 'string') {
			formData.append('certificate_url', fileToUse);
		}
	}
	
	return true;
};

/**
 * Process professional experience step data
 */
export const processProfessionalExperienceStep = (formData, stepData) => {
	if (!stepData) return;
	appendObjectToFormData(formData, stepData, 'experience');
};

/**
 * Process single recommendation
 */
const processRecommendation = (formData, recData, prefix) => {
	if (!recData) return true;
	
	// Process recommendation fields
	appendObjectToFormData(formData, recData, prefix, ['file']);
	
	// Handle file
	if (recData.file) {
		if (recData.file instanceof File) {
			const validation = validateFileType(recData.file, FILE_TYPES.CERTIFICATE, 5);
			if (!validation.isValid) {
				toast.error(`${prefix} Letter Error: ${validation.error}`);
				return false;
			}
			formData.append(`${prefix}_letter`, recData.file);
		} else if (typeof recData.file === 'string') {
			formData.append(`${prefix}_letter_url`, recData.file);
		}
	}
	
	return true;
};

/**
 * Process recommendations step data
 */
export const processRecommendationsStep = (formData, stepData) => {
	if (!stepData) return true;
	
	const success1 = processRecommendation(formData, stepData.recommendation1, 'recommendation1');
	const success2 = processRecommendation(formData, stepData.recommendation2, 'recommendation2');
	
	return success1 && success2;
};

/**
 * Process fellowship information step data
 */
export const processFellowshipInfoStep = (formData, stepData) => {
	if (!stepData) return true;
	
	// Process program
	if (stepData.programAppliedFor) {
		formData.append('fellowship_program', stepData.programAppliedFor);
	}
	
	// Process themes
	if (stepData.themes && typeof stepData.themes === 'object') {
		const selectedThemeKeys = Object.entries(stepData.themes)
			.filter(([key, value]) => value === true)
			.map(([key]) => key);
		
		if (selectedThemeKeys.length > 0) {
			formData.append('fellowship_themes', JSON.stringify(selectedThemeKeys));
		}
	}
	
	// Process others field
	if (stepData.themes?.others && stepData.othersField?.trim()) {
		formData.append('fellowship_othersField', stepData.othersField.trim());
	}
	
	// Process book proposal
	if (stepData.bookProposal) {
		if (stepData.bookProposal instanceof File) {
			const validation = validateFileType(stepData.bookProposal, FILE_TYPES.FELLOWSHIP, 5);
			if (!validation.isValid) {
				toast.error(`Book Proposal Error: ${validation.error}`);
				return false;
			}
			formData.append('fellowship_bookProposal', stepData.bookProposal);
		} else if (typeof stepData.bookProposal === 'string') {
			formData.append('fellowship_bookProposal_url', stepData.bookProposal);
		}
	}
	
	// Process writing sample
	if (stepData.writingSample) {
		if (stepData.writingSample instanceof File) {
			const validation = validateFileType(stepData.writingSample, FILE_TYPES.FELLOWSHIP, 5);
			if (!validation.isValid) {
				toast.error(`Writing Sample Error: ${validation.error}`);
				return false;
			}
			formData.append('fellowship_writingSample', stepData.writingSample);
		} else if (typeof stepData.writingSample === 'string') {
			formData.append('fellowship_writingSample_url', stepData.writingSample);
		}
	}
	
	return true;
};

/**
 * Process additional information step data
 */
export const processAdditionalInfoStep = (formData, stepData) => {
	if (!stepData) return;
	
	// Process publications
	if (Array.isArray(stepData.publications)) {
		const validPublications = stepData.publications
			.filter(pub => pub.name?.trim())
			.map(pub => ({
				name: pub.name.trim(),
				url: pub.url?.trim() || '',
				file_url: pub.file_url || '',
			}));
		
		if (validPublications.length > 0) {
			formData.append('publications', JSON.stringify(validPublications));
		}
		
		// Handle publication files - send each with unique index
		stepData.publications.forEach((pub, index) => {
			if (pub.file instanceof File) {
				formData.append(`publication_file_${index}`, pub.file);
			}
		});
	}
	
	// Process social media
	if (Array.isArray(stepData.socialMedia)) {
		const validSocial = stepData.socialMedia
			.filter(soc => {
				// Keep NA entries
				if (soc.platform === 'NA') return true;
				// Otherwise, require both platform and URL
				return soc.platform?.trim() && soc.url?.trim();
			})
			.map(soc => ({
				platform: soc.platform.trim(),
				url: soc.platform === 'NA' ? 'NA' : soc.url.trim(),
			}));
		
		if (validSocial.length > 0) {
			formData.append('socialMedia', JSON.stringify(validSocial));
		}
	}
	
	// Handle additional document
	appendFileOrUrl(formData, stepData.additionalDocument, 'additional_document', null);
	
	// Handle terms acceptance
	if (stepData.termsAccepted) {
		formData.append('terms_accepted', 'true');
	}
};

/**
 * Process step data based on step name
 */
export const processStepData = (stepName, formData, stepData, originalData = null) => {
	const processors = {
		personalInfo: () => processPersonalInfoStep(formData, stepData),
		educationalBackground: () => processEducationStep(formData, stepData, originalData),
		professionalExperience: () => processProfessionalExperienceStep(formData, stepData),
		recommendations: () => processRecommendationsStep(formData, stepData),
		fellowshipInformation: () => processFellowshipInfoStep(formData, stepData),
		additionalInformation: () => processAdditionalInfoStep(formData, stepData),
	};
	
	const processor = processors[stepName];
	if (processor) {
		return processor();
	}
	
	return true;
};
